/**
 * One symbol's news and sentiment, fetched on demand and cached per request.
 *
 * The same shape as `symbol-fundamentals.ts` and for the same reasons: the
 * cache is keyed on the request rather than held in a component, so the PIP,
 * the data panel and a detached window showing one ticker share one fetch; a
 * failure is not cached, because `undefined` usually means qv-data is not up
 * yet; and the symbol is re-checked on resolution so switching ticker mid-flight
 * cannot paint the previous symbol's headlines under the new one's heading.
 *
 * **News and Sentiment read the same document.** The vendor attaches sentiment
 * to each article, so two caches would be the same call twice.
 */

import { useEffect, useState } from "react";

import { fetchSymbolNews, type SymbolNews } from "../api/symbols";

const cache = new Map<string, Promise<SymbolNews | undefined>>();

function key(symbol: string, releases: boolean): string {
  return `${symbol.toUpperCase()}|${releases ? "full" : "headlines"}`;
}

export function loadNews(
  symbol: string,
  releases = true,
): Promise<SymbolNews | undefined> {
  const id = key(symbol, releases);
  const existing = cache.get(id);
  if (existing) return existing;

  const pending = fetchSymbolNews(symbol, { releases }).then((result) => {
    if (result === undefined) cache.delete(id);
    return result;
  });
  cache.set(id, pending);
  return pending;
}

/** Drop everything. For tests, and for a host that knows the feed moved. */
export function invalidateNews(): void {
  cache.clear();
}

/** React binding. `undefined` while loading; a document once it lands. */
export function useNews(symbol: string, releases = true): SymbolNews | undefined {
  const [data, setData] = useState<SymbolNews | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setData(undefined);
    void loadNews(symbol, releases).then((result) => {
      if (live) setData(result);
    });
    return () => {
      live = false;
    };
  }, [symbol, releases]);

  return data;
}
